import { reactive } from 'vue';

export interface EditorUploadState {
  status: 'uploading' | 'error';
  progress: number;
  errorMessage?: string;
  /** Object URL of the local file — instant preview for images while uploading. */
  previewUrl?: string;
}

/**
 * Module-scoped registry of in-flight editor uploads keyed by the transient
 * `uploadId` node attr. NodeView components read it directly — progress ticks
 * must not create ProseMirror transactions (they'd spam history/autosave).
 */
const registry = reactive(new Map<string, EditorUploadState>());

export const getUploadState = (uploadId: string | null | undefined): EditorUploadState | undefined => {
  return uploadId ? registry.get(uploadId) : undefined;
};

export const registerUpload = (uploadId: string, previewUrl?: string): void => {
  registry.set(uploadId, {
    status: 'uploading',
    progress: 0,
    previewUrl,
  });
};

export const updateUploadProgress = (uploadId: string, progress: number): void => {
  const entry = registry.get(uploadId);
  if (entry) {
    entry.progress = progress;
  }
};

export const failUpload = (uploadId: string, errorMessage: string): void => {
  const entry = registry.get(uploadId);
  if (entry) {
    entry.status = 'error';
    entry.errorMessage = errorMessage;

    // The error card never shows the preview — release the blob right away.
    if (entry.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl);
      entry.previewUrl = undefined;
    }
  }
};

/** Drop the entry and release its object URL (upload finished or node removed). */
export const clearUpload = (uploadId: string): void => {
  const entry = registry.get(uploadId);
  if (entry?.previewUrl) {
    URL.revokeObjectURL(entry.previewUrl);
  }
  registry.delete(uploadId);
};
